/**
 * useSchedulingOptions Hook
 *
 * Manages scheduling state and logic for the approval flow.
 * Fetches auto-scheduled time from backend (including quiet hours),
 * handles manual overrides, and detects cascading reschedule scenarios.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { addMinutes } from 'date-fns';
import { api } from '../services/api';
import {
  validateWithCascadeOption,
  calculateCascadingReschedules
} from '../utils/scheduleValidation';

const DEFAULT_POST_INTERVAL_HOURS = 24;
const IMMEDIATE_OFFSET_MINUTES = 5;

/**
 * Hook for managing scheduling options in the approval modal.
 *
 * @param {string} accountId - Instagram account ID
 * @param {boolean} isOpen - Whether the modal is open
 * @returns {Object} Scheduling state and handlers
 */
export function useSchedulingOptions(accountId, isOpen) {
  // State
  const [autoScheduledTime, setAutoScheduledTime] = useState(null);
  const [manualScheduledTime, setManualScheduledTime] = useState(null);
  const [scheduledStories, setScheduledStories] = useState([]);
  const [postIntervalHours, setPostIntervalHours] = useState(DEFAULT_POST_INTERVAL_HOURS);
  const [schedulingDescription, setSchedulingDescription] = useState('');
  const [quietHoursApplied, setQuietHoursApplied] = useState(false);
  const [quietHoursAdjustment, setQuietHoursAdjustment] = useState(null);
  const [accountTimezone, setAccountTimezone] = useState('UTC');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derived state
  const isManualMode = manualScheduledTime !== null;
  const scheduledTime = manualScheduledTime || autoScheduledTime;

  // Calculate cascade info when manual time is selected
  const cascadeInfo = useMemo(() => {
    if (!isManualMode || !manualScheduledTime || scheduledStories.length === 0) {
      return { needsCascade: false, affectedStories: [], newSchedules: [] };
    }

    return calculateCascadingReschedules(
      manualScheduledTime,
      null, // No current story ID (this is a new approval)
      scheduledStories,
      postIntervalHours
    );
  }, [isManualMode, manualScheduledTime, scheduledStories, postIntervalHours]);

  // Validate manual time selection
  const validationResult = useMemo(() => {
    if (!isManualMode || !manualScheduledTime) {
      return { isValid: true };
    }

    return validateWithCascadeOption(
      manualScheduledTime,
      null, // No current story ID
      scheduledStories,
      postIntervalHours
    );
  }, [isManualMode, manualScheduledTime, scheduledStories, postIntervalHours]);

  // Fetch data when modal opens
  useEffect(() => {
    if (!isOpen || !accountId) {
      return;
    }

    let isMounted = true;
    const abortController = new AbortController();

    const fetchSchedulingData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch schedule preview and scheduled stories in parallel
        const [previewResult, storiesResult] = await Promise.all([
          api.getSchedulePreview(accountId, { signal: abortController.signal }),
          api.getStories({ status: 'scheduled', instagram_account_id: accountId }, { signal: abortController.signal }).catch(() => ({ stories: [] }))
        ]);

        if (!isMounted) return;

        // Parse schedule preview from backend
        if (previewResult) {
          const scheduledFor = new Date(previewResult.scheduled_for);
          setAutoScheduledTime(scheduledFor);
          setPostIntervalHours(previewResult.posting_interval_hours || DEFAULT_POST_INTERVAL_HOURS);
          setSchedulingDescription(previewResult.description || '');
          setQuietHoursApplied(previewResult.quiet_hours_applied || false);
          setQuietHoursAdjustment(previewResult.quiet_hours_adjustment || null);
          setAccountTimezone(previewResult.timezone || 'UTC');
        } else {
          // Fallback: schedule 5 minutes from now
          setAutoScheduledTime(addMinutes(new Date(), IMMEDIATE_OFFSET_MINUTES));
        }

        // Store scheduled stories for cascade detection
        const stories = storiesResult?.stories || [];
        setScheduledStories(stories);

      } catch (err) {
        if (err.name === 'AbortError') return;
        if (isMounted) {
          console.error('Failed to fetch scheduling data:', err);
          setError(err.message || 'Failed to load scheduling data');
          // Fallback: schedule 5 minutes from now
          setAutoScheduledTime(addMinutes(new Date(), IMMEDIATE_OFFSET_MINUTES));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSchedulingData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [isOpen, accountId]);

  // Reset manual time when modal closes
  useEffect(() => {
    if (!isOpen) {
      setManualScheduledTime(null);
    }
  }, [isOpen]);

  /**
   * Set manual scheduled time
   */
  const setManualTime = useCallback((time) => {
    setManualScheduledTime(time);
  }, []);

  /**
   * Clear manual time (revert to auto)
   */
  const clearManualTime = useCallback(() => {
    setManualScheduledTime(null);
  }, []);

  /**
   * Execute cascade reschedule if needed
   * Returns a promise that resolves when cascade is complete
   */
  const executeCascadeReschedule = useCallback(async () => {
    if (!cascadeInfo.needsCascade || cascadeInfo.newSchedules.length === 0) {
      return { success: true, rescheduled: 0 };
    }

    try {
      const reschedules = cascadeInfo.newSchedules.map(schedule => ({
        story_id: schedule.storyId,
        scheduled_for: schedule.newDate.toISOString()
      }));

      await api.bulkRescheduleStories(accountId, reschedules);

      return {
        success: true,
        rescheduled: reschedules.length
      };
    } catch (err) {
      console.error('Failed to execute cascade reschedule:', err);
      throw err;
    }
  }, [accountId, cascadeInfo]);

  return {
    // State
    autoScheduledTime,
    manualScheduledTime,
    scheduledTime,
    isManualMode,
    cascadeInfo,
    validationResult,
    postIntervalHours,
    scheduledStories,
    schedulingDescription,
    quietHoursApplied,
    quietHoursAdjustment,
    accountTimezone,
    isLoading,
    error,

    // Actions
    setManualTime,
    clearManualTime,
    executeCascadeReschedule
  };
}

export default useSchedulingOptions;
